import { useSearchParams } from "react-router";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import {
  SUBMISSION_STATUS_LABEL,
  VIEW_COLUMN_LABEL,
  type SubmissionStatus,
} from "@colab/shared";
import { formatRelative, cn } from "@/lib/utils";
import type {
  AdminSubmissionRow,
  AdminSubmissionsSortColumn,
} from "./types";

// Phase 7 sub-bucket 19 — refactored to a column-key-driven render map per
// the legacy `components/admin/submissions/SubmissionsTable.tsx` shape, with
// `visibleColumns?: string[]` defaulting to the full set so existing call
// sites without a saved view keep their legacy column layout.
//
// Row click sets `?submission=<id>` (drawer-as-shareable-route). Sort column
// + direction live in `?sort=` / `?dir=`. Sort columns map to DB-side names
// where the column alias and DB column differ — `submitted_at` is sorted on
// `created_at`, `decided_at` is its own real column.

type Props = {
  rows: AdminSubmissionRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Saved-view-driven column visibility + ordering. Falls back to the
   *  legacy full set when omitted (no saved view active). */
  visibleColumns?: string[];
};

type ColumnDef = {
  key: string;
  sortable?: boolean;
  align?: "left" | "right";
  /** Real DB column the sort param translates to (defaults to `key`). */
  sortBy?: AdminSubmissionsSortColumn;
  render: (row: AdminSubmissionRow) => React.ReactNode;
};

const COLUMNS: Record<string, ColumnDef> = {
  form_name: {
    key: "form_name",
    render: (s) => (
      <span className="font-medium text-foreground">{s.form_name}</span>
    ),
  },
  partner_name: {
    key: "partner_name",
    render: (s) => (
      <span className="text-muted-foreground">
        {s.partner_name ?? "Anonymous"}
      </span>
    ),
  },
  status: {
    key: "status",
    sortable: true,
    sortBy: "status",
    render: (s) => <StatusPill status={s.status} />,
  },
  submitted_at: {
    key: "submitted_at",
    sortable: true,
    sortBy: "created_at",
    align: "right",
    render: (s) => (
      <span className="text-xs text-muted-foreground">
        {formatRelative(s.submitted_at)}
      </span>
    ),
  },
  decided_at: {
    key: "decided_at",
    sortable: true,
    sortBy: "decided_at",
    align: "right",
    render: (s) => (
      <span className="text-xs text-muted-foreground">
        {s.decided_at ? formatRelative(s.decided_at) : "—"}
      </span>
    ),
  },
  decided_by: {
    key: "decided_by",
    render: (s) => (
      <span className="text-muted-foreground">
        {s.decided_by_name ?? "—"}
      </span>
    ),
  },
  downstream_kind: {
    key: "downstream_kind",
    render: (s) => (
      <span className="text-muted-foreground">
        {s.downstream_kind ?? "—"}
      </span>
    ),
  },
};

const DEFAULT_VISIBLE = [
  "form_name",
  "partner_name",
  "status",
  "submitted_at",
  "decided_at",
  "decided_by",
  "downstream_kind",
] as const;

export function SubmissionsTable({
  rows,
  selectedId,
  onSelect,
  visibleColumns,
}: Props) {
  const cols = (visibleColumns && visibleColumns.length > 0
    ? visibleColumns
    : (DEFAULT_VISIBLE as readonly string[])
  )
    .map((k) => COLUMNS[k])
    .filter((c): c is ColumnDef => Boolean(c));

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {cols.map((c) =>
              c.sortable && c.sortBy ? (
                <SortableHeader
                  key={c.key}
                  name={c.sortBy}
                  label={VIEW_COLUMN_LABEL[c.key] ?? c.key}
                  align={c.align ?? "left"}
                />
              ) : (
                <th
                  key={c.key}
                  className={`px-4 py-2.5 ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {VIEW_COLUMN_LABEL[c.key] ?? c.key}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((s) => (
            <tr
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={cn(
                "group cursor-pointer transition-colors hover:bg-muted/30",
                selectedId === s.id && "bg-muted/30",
              )}
            >
              {cols.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-3 ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {c.render(s)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: SubmissionStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        status === "pending" &&
          "bg-amber-100 text-amber-900 dark:bg-amber-500/10 dark:text-amber-300",
        status === "accepted" &&
          "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-300",
        status === "declined" &&
          "bg-rose-100 text-rose-900 dark:bg-rose-500/10 dark:text-rose-300",
      )}
    >
      {SUBMISSION_STATUS_LABEL[status]}
    </span>
  );
}

function SortableHeader({
  name,
  label,
  align = "left",
}: {
  name: AdminSubmissionsSortColumn;
  label: string;
  align?: "left" | "right";
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSort = searchParams.get("sort") ?? "created_at";
  const currentDir = searchParams.get("dir") ?? "desc";
  const isActive = currentSort === name;

  function toggle() {
    const next = new URLSearchParams(searchParams);
    if (!isActive) {
      next.set("sort", name);
      next.set("dir", "asc");
    } else {
      next.set("dir", currentDir === "asc" ? "desc" : "asc");
    }
    setSearchParams(next, { replace: true });
  }

  const Icon = !isActive
    ? ArrowUpDown
    : currentDir === "asc"
      ? ArrowUp
      : ArrowDown;

  return (
    <th
      className={`px-4 py-2.5 ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={toggle}
        className={`inline-flex items-center gap-1 transition hover:text-foreground ${
          isActive ? "text-foreground" : ""
        }`}
      >
        {label}
        <Icon className="h-3 w-3 opacity-70" />
      </button>
    </th>
  );
}
